import { isEmpty } from 'lodash';
import { Catalog, CatalogTreeViewItem } from 'src/types/catalog/Catalog';

const catalogTypeConfig: Record<number, { displayText: string; groupId: string }> = {
  1: { displayText: 'HOSTED CATALOGS', groupId: 'hosted-catalogs' },
  2: { displayText: 'EXTERNAL CATALOGS (PUNCHOUTS)', groupId: 'external-catalogs' },
  3: { displayText: 'OFFLINE CATALOG', groupId: 'offline-catalog' },
};

export const ALL_CATALOGS_TREE_ITEM_ID = 'all-catalogs';

export const getNumericCatalogId = (
  catalogId: number | 'all' | null | undefined
): number | null => (typeof catalogId === 'number' ? catalogId : null);

export interface TransformCatalogsToTreeDataOptions {
  includeAllCatalogs?: boolean;
  allCatalogsLabel?: string;
}

export const transformCatalogsToTreeData = (
  catalogs: Catalog[],
  options?: TransformCatalogsToTreeDataOptions
): CatalogTreeViewItem[] => {
  // Group catalogs by catalogType
  const catalogsGroupedByType = catalogs.reduce((acc, catalog) => {
    if (!acc[catalog.catalogType]) {
      acc[catalog.catalogType] = [];
    }
    acc[catalog.catalogType].push(catalog);
    return acc;
  }, {} as Record<number, Catalog[]>);

  const treeData: CatalogTreeViewItem[] = [];

  // Define the order: 1, 3, 2
  // NOTE: External Catalogs (Punchouts, type 2) are temporarily hidden from the
  // dropdown. Add `2` back to this array when the feature is ready to ship.
  const catalogTypeOrder = [1, 3];

  catalogTypeOrder.forEach((catalogType) => {
    const catalogsOfType = catalogsGroupedByType[catalogType];
    if (!catalogsOfType) return;

    const config = catalogTypeConfig[catalogType];
    const catalogItems = catalogsOfType
      .sort((a, b) => a.catalogName.localeCompare(b.catalogName))
      .map((catalog) => ({
        text: catalog.catalogName,
        id: `catalog-${catalog.catalogId}`,
        catalogId: catalog.catalogId,
        catalogName: catalog.catalogName,
        catalogType: catalog.catalogType,
      }));
    treeData.push({
      text: config.displayText,
      id: config.groupId,
      expanded: true,
      isGroupHeader: true,
      items: catalogItems,
    });
  });

  if (options?.includeAllCatalogs) {
    const allCatalogsLabel = options.allCatalogsLabel ?? 'All Catalogs';

    return [
      {
        text: allCatalogsLabel,
        id: ALL_CATALOGS_TREE_ITEM_ID,
        catalogId: 'all',
        catalogName: allCatalogsLabel,
      },
      ...treeData,
    ];
  }

  return treeData;
};

export const findCatalogTreeItem = (
  items: CatalogTreeViewItem[],
  catalogId: number | 'all'
): CatalogTreeViewItem | null => {
  for (const item of items) {
    if (item.catalogId === catalogId && !item.isGroupHeader) {
      return item;
    }

    if (item.items) {
      const found = findCatalogTreeItem(item.items, catalogId);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const getCatalogDropdownPlaceholder = (
  catalogs: unknown[] | undefined,
  loadingCatalogs: boolean,
  t: (key: string) => string
): string => {
  if (loadingCatalogs) {
    return t('Loading...');
  }

  if (isEmpty(catalogs)) {
    return t('No Catalogs');
  }

  return t('Select Catalog');
};
